/**
 * Loads the Google Maps JS API exactly once across the app and resolves a
 * Promise to the global `google` namespace. Subsequent calls reuse the
 * existing script tag.
 */

declare global {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  interface Window {
    google?: any;
    __mapsLoaderPromise?: Promise<unknown>;
  }
}

export function loadGoogleMaps(apiKey: string): Promise<typeof window.google> {
  if (typeof window === 'undefined') return Promise.reject(new Error('no_window'));
  if (window.google?.maps) return Promise.resolve(window.google);
  if (window.__mapsLoaderPromise) return window.__mapsLoaderPromise as Promise<typeof window.google>;

  const p = new Promise<typeof window.google>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=marker&v=weekly`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google?.maps) resolve(window.google);
      else reject(new Error('google_maps_unavailable'));
    };
    script.onerror = () => reject(new Error('maps_script_load_failed'));
    document.head.appendChild(script);
  });
  window.__mapsLoaderPromise = p;
  return p;
}
